/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Modal, ModalBody, ModalContent, ModalHeader, ModalTitle, ModalFooter } from "../ui/Modal";
import Button from "../ui/Button";
import Text from "../ui/Text";
import Stack from "../ui/Stack";
import FormatPrice from "../formatters/FormatPrice";
import { ROUTES } from "../../constants/constants";
import ButtonGroup from "../ui/ButtonGroup";
import { useMembershipCheckout } from "../../hooks/checkout/useMembershipCheckout";
import MembershipServiceCard from "./MembershipServiceCard";
import { useTranslate } from "../../hooks/useTranslate";
import { formatMembershipDuration } from "../../utils/helpers/membership";

const MembershipRequiredModal = ({ isOpen, onClose, service, requiredMemberships }) => {
  const navigate = useNavigate();
  const { checkout } = useMembershipCheckout();
  const { _t } = useTranslate();

  const isSingle = requiredMemberships?.length === 1;
  const onlyMembership = isSingle ? requiredMemberships[0] : null;

  const handleMembershipAction = () => {
    if (isSingle && onlyMembership) {
      checkout({ membership: onlyMembership, sourceServiceId: service?.id });
      onClose?.();
    } else {
      onClose?.();
      navigate(`${ROUTES.MEMBERSHIPS_CHOOSE}?service=${service?.id ?? ""}`);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Membership required</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Stack $gap="1rem">
            <Text color="dark">This service requires a membership. Unlock access with a membership.</Text>
            <MembershipServiceCard service={service} />
          </Stack>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup itemsWidth="half">
            <Button variant="outline" onClick={onClose} title="Maybe later" />
            {isSingle && (
              <Button
                onClick={handleMembershipAction}
                title={
                  <>
                    <FormatPrice value={onlyMembership.first_price} currency={onlyMembership.currency} />/
                    {formatMembershipDuration(onlyMembership.duration, onlyMembership.duration_type, _t)}
                  </>
                }
              />
            )}
            {!isSingle && (
              <Button variant="primary" onClick={handleMembershipAction} title="Check memberships" />
            )}
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal >
  );
};

export default MembershipRequiredModal;
